import React from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface CircleEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor: string;
  opacity: number;
  speed: number;
  mode: TimerMode;
  showMilliseconds?: boolean;
  totalMs?: number;
  currentMs?: number;
}

const CircleEffect: React.FC<CircleEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity,
  speed,
  mode,
  showMilliseconds = false,
  totalMs,
  currentMs
}) => {
  // 计算基本参数
  const center = size / 2;
  const strokeWidth = Math.max(4, size / 20); // 根据大小自适应线宽
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  
  // 根据模式调整方向和效果
  const isCountdown = mode === 'countdown';
  
  // 使用弹簧动画使过渡更平滑，根据speed参数调整动画速度
  const springConfig = {
    stiffness: 400 * speed,
    damping: 30 * Math.sqrt(speed),
  };
  
  // 重新计算进度，如果提供了totalMs和currentMs
  let calculatedProgress = progress;
  if (totalMs !== undefined && currentMs !== undefined && totalMs > 0) {
    if (isCountdown) {
      // 倒计时模式：从1到0
      if (showMilliseconds) {
        // 显示毫秒时使用精确值
        calculatedProgress = Math.max(0, Math.min(1, currentMs / totalMs));
      } else {
        // 不显示毫秒时，模拟向上取整的效果
        const hoursPart = Math.floor(currentMs / (1000 * 60 * 60));
        const minutesPart = Math.floor((currentMs % (1000 * 60 * 60)) / (1000 * 60));
        const secondsPart = Math.ceil((currentMs % (1000 * 60)) / 1000);
        
        const displayTotalSeconds = hoursPart * 3600 + minutesPart * 60 + secondsPart;
        const totalSeconds = Math.ceil(totalMs / 1000);
        
        calculatedProgress = Math.max(0, Math.min(1, displayTotalSeconds / totalSeconds));
      }
    } else {
      // 秒表模式：从0到1（如果设置了总时间）或无限增长
      if (totalMs > 0) {
        calculatedProgress = Math.min(1, currentMs / totalMs);
      } else {
        // 如果没有设置总时间，使用时间本身作为进度的基础
        // 每小时循环一次完整的特效
        const oneHourMs = 60 * 60 * 1000;
        calculatedProgress = (currentMs % oneHourMs) / oneHourMs;
      }
    }
  }
  
  const springProgress = useSpring(isCountdown ? calculatedProgress : 1 - calculatedProgress, springConfig);
  
  // 将进度转换为描边偏移量
  const strokeDashoffset = useTransform(
    springProgress,
    (value: number) => circumference * value
  );
  
  // 根据进度动态调整颜色的饱和度
  const dynamicOpacity = Math.max(0.3, Math.min(1, opacity));
  
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: dynamicOpacity,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* 背景圆 */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={secondaryColor}
          strokeWidth={strokeWidth}
          strokeOpacity={0.3}
        />
        
        {/* 进度圆 */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          // 从12点钟方向开始，根据模式决定旋转方向
          transform={`rotate(${isCountdown ? -90 : 90} ${center} ${center})`}
          strokeLinecap="round"
        />
        
        {/* 可选：在进度条前端添加一个小圆点 */}
        {calculatedProgress > 0 && calculatedProgress < 1 && (
          <motion.circle
            cx={center}
            cy={strokeWidth / 2}
            r={strokeWidth * 0.8}
            fill={color}
            transform={`rotate(${(isCountdown ? -90 : 90) + 360 * (isCountdown ? calculatedProgress : 1 - calculatedProgress)} ${center} ${center})`}
          />
        )}
      </svg>
    </div>
  );
};

export default CircleEffect; 